import { useState } from "react";
import { createDateFromDDMMYYYY, formatDateYYYYMMDD } from "@icarius-utils/date";
import * as yup from 'yup';

const yupNumberDefinition = yup.number().transform((value, originalValue) => originalValue === "" || originalValue === null ? undefined : value);

const useHandleForm = (data, userFieldsDefinition, submitCallback, invalidDataCallback) => {

  const isCreate = !Boolean(data);
  const userFieldNames = userFieldsDefinition.map((userField) => userField.name);

  const createInitialData = () => {

    const getInitialFieldData = (fieldName) => {
      if (userFieldNames.includes(fieldName)) {
        const userFieldDefinition = userFieldsDefinition.find((userField) => userField.name === fieldName);
        const userFieldValue = data?.userFields[fieldName];

        if (!userFieldValue) {
          if (userFieldDefinition.type === "date") return null;
          if (["integer", "float"].includes(userFieldDefinition.type)) return 0;
          return ""
        }

        if (userFieldDefinition.type === "date") return createDateFromDDMMYYYY(userFieldValue);
        return userFieldValue;
      }

      if ([
        "points",
        "assistancePercentageRequired",
        "knowledgeCertificateMinScore",
        "knowledgeCertificateMaxScore",
        "knowledgeCertificateMinScoreToPass"
      ].includes(fieldName)) return data?.[fieldName] || 0;
      if (["published", "hasAssistanceCertificate", "hasKnowledgeCertificate"].includes(fieldName)) return data?.[fieldName] || false;
      if (fieldName === "themes") return data?.[fieldName] || [];
      if (fieldName === "skills") return data?.[fieldName] || [];
      if (["assistanceCertificateFile", "knowledgeCertificateFile", "previewFile"].includes(fieldName)) {
        if (data?.[fieldName]) return [data[fieldName]];
        return [];
      }
      return data?.[fieldName] || "";
    }

    const fieldNames = [
      "name",
      "published",
      "area",
      "themes",
      "description",
      "hasAssistanceCertificate",
      "assistanceCertificateFile",
      "assistancePercentageRequired",
      "hasKnowledgeCertificate",
      "knowledgeCertificateFile",
      "knowledgeCertificateMinScore",
      "knowledgeCertificateMaxScore",
      "knowledgeCertificateMinScoreToPass",
      "points",
      "previewFile",
      "previewFromGallery",
      "skills",
      ...userFieldNames,
    ];

    return fieldNames.reduce((initialData, fieldName) => {
      return ({
        ...initialData,
        [fieldName]: getInitialFieldData(fieldName)
      })
    }, {});
  }

  const [formData, setFormData] = useState(() => createInitialData());

  const previewFileComesFromBackend = Boolean(data?.previewFile) && formData.previewFile?.[0] === data?.previewFile;

  const baseFieldsAreValid = async () => {
    const schema = yup.object().shape({
      name: yup.string().required(),
      area: yup.string().required(),
      points: yupNumberDefinition.min(0).max(999999999),
      themes: yup.array().of(yup.string()).min(1).required(),
      description: yup.string().required(),
      hasAssistanceCertificate: yup.boolean(),
      assistanceCertificateFile: yup.array().nullable()
        .when('hasAssistanceCertificate', {
          is: (value) => value === true,
          then: yup.array().min(1).required(),
        }),
      assistancePercentageRequired: yupNumberDefinition
        .when('hasAssistanceCertificate', {
          is: (value) => value === true,
          then: yupNumberDefinition.min(0).max(100).required(),
        }),
      hasKnowledgeCertificate: yup.boolean(),
      knowledgeCertificateFile: yup.array().nullable()
        .when('hasKnowledgeCertificate', {
          is: (value) => value === true,
          then: yup.array().min(1).required(),
        }),
      knowledgeCertificateMinScore: yupNumberDefinition
        .when('hasKnowledgeCertificate', {
          is: (value) => value === true,
          then: yupNumberDefinition.min(0).max(100).required(),
        }),
      knowledgeCertificateMaxScore: yupNumberDefinition
        .when('hasKnowledgeCertificate', {
          is: (value) => value === true,
          then: yupNumberDefinition.min(formData.knowledgeCertificateMinScore).max(100).required(),
        }),
      knowledgeCertificateMinScoreToPass: yupNumberDefinition
        .when('hasKnowledgeCertificate', {
          is: (value) => value === true,
          then: yupNumberDefinition.min(formData.knowledgeCertificateMinScore).max(formData.knowledgeCertificateMaxScore).required(),
        }),
    });

    return await schema.validate(formData, { abortEarly: false })
      .then((castData) => castData)
      .catch(err => {
        if (err instanceof yup.ValidationError) {
          err.inner.forEach(error => {
            console.log(error.message);
          });
          return false;
        }
      });
  }

  const userFieldsAreValid = () => {
    let isValid = true;

    userFieldsDefinition.forEach((userField) => {
      const userFieldValue = formData[userField.name];

      // si no tiene valor
      if ((!userFieldValue && userFieldValue !== 0) || userFieldValue?.length === 0 || /^\s+$/.test(userFieldValue)) {
        if (userField.required) {
          isValid = false;
        }
      } else {
        //si tiene valor, pregunto por min y max segun el type
        switch (userField.type) {
          case "text":
            if (userField.max && userFieldValue.length > userField.max) {
              isValid = false;
            }

            break;
          case "integer":
          case "float":
            if (userField.max && userFieldValue > userField.max) {
              isValid = false;
            }

            if (userField.min !== null && userFieldValue < userField.min) {
              isValid = false;
            }
            break;

          case "date":
            if (!(userFieldValue instanceof Date && !isNaN(userFieldValue))) {
              isValid = false;
            } else {
              if (userField.min) {
                if (userFieldValue < new Date(formatDateYYYYMMDD(userField.min))) {
                  isValid = false;
                }
              }

              if (userField.max) {
                if (userFieldValue > new Date(formatDateYYYYMMDD(userField.max))) {
                  isValid = false;
                }
              }
            }
            break;
          case "options":
            const optionOk = userField?.options?.find((item) => item.key === userFieldValue);
            if (!optionOk) {
              isValid = false;
            }
            break;
          default: break;
        }
      }
    });

    return isValid;
  };

  const getFormattedUserFieldsToSend = () => {
    if (!userFieldsDefinition.length) return null;
    const userFieldsObject = {};

    userFieldsDefinition.forEach((userFieldDefinition) => {
      let userFieldValue = formData[userFieldDefinition.name];

      if (userFieldDefinition.type === "date") {
        userFieldValue = formatDateYYYYMMDD(userFieldValue);
      }

      userFieldsObject[userFieldDefinition.name] = userFieldValue;
    })

    return userFieldsObject;
  }

  const getFormDataToSend = (dataToSend) => {
    let formDataToSend = new FormData();

    const allBooleanFieldNames = [
      "published",
      "hasAssistanceCertificate",
      "hasKnowledgeCertificate",
    ]

    Object.keys(dataToSend).forEach((fieldName) => {
      const fieldValue = dataToSend[fieldName];

      // array y objetos se pasan como json.stringify
      if (["userFields", "themes", "skills"].includes(fieldName)) {
        formDataToSend.append(fieldName, JSON.stringify(fieldValue));
        return;
      }

      if (fieldName === "previewFile" && fieldValue === null) {
        formDataToSend.append(fieldName, null);
        return;
      }

      // los no bool sin datos se ponen como ""
      if ((!fieldValue && fieldValue !== 0) && !allBooleanFieldNames.includes(fieldName)) {
        formDataToSend.append(fieldName, '');
        return;
      }

      formDataToSend.append(fieldName, fieldValue);
    });

    return formDataToSend;
  }

  const submit = async (formDataToSubmit) => {
    if (await baseFieldsAreValid() && userFieldsAreValid()) {
      const dataToSend = { ...formDataToSubmit };

      // si vino archivo de back y es el mismo archivo que tengo en el form, no mandarlo
      if (previewFileComesFromBackend) {
        delete dataToSend.previewFile;
      } else {
        dataToSend.previewFile = formData.previewFile?.[0] || null; // se setea el archivo o se manda null si se borró o nunca se seleccionó archivo
      }

      if (formData.hasAssistanceCertificate) {
        dataToSend.assistanceCertificateFile = formData.assistanceCertificateFile[0];
      } else {
        delete dataToSend.assistanceCertificateFile;
        delete dataToSend.assistancePercentageRequired;
      }

      if (formData.hasKnowledgeCertificate) {
        dataToSend.knowledgeCertificateFile = formData.knowledgeCertificateFile[0];
      } else {
        delete dataToSend.knowledgeCertificateFile
        delete dataToSend.knowledgeCertificateMinScore
        delete dataToSend.knowledgeCertificateMaxScore
        delete dataToSend.knowledgeCertificateMinScoreToPass
      }

      const formattedUserFieldsToSend = getFormattedUserFieldsToSend();
      if (formattedUserFieldsToSend) {
        // borrar los userFields sueltos porque se ponen despues en un objeto
        userFieldNames.forEach((userFieldName) => {
          delete dataToSend[userFieldName];
        });

        dataToSend.userFields = formattedUserFieldsToSend;
      }

      submitCallback(getFormDataToSend(dataToSend));
      return true;
    } else {
      invalidDataCallback();
      return false;
    }
  }

  const setFormValue = (value, fieldName) => {
    if (fieldName === "previewFromGallery") {
      setFormData({
        ...formData,
        previewFile: [],
        [fieldName]: value,
      });
      return;
    }

    if (fieldName === "previewFile") {
      setFormData({
        ...formData,
        previewFromGallery: "",
        [fieldName]: value,
      });
      return;
    }

    if (fieldName === "themes") {
      if (!value.trim()) return; // si el valor esta vacio, ignorarlo
      setFormData((prev) => {
        const newThemes = prev.themes.includes(value) ? prev.themes.filter((theme) => theme !== value) : [...prev.themes, value];
        return {
          ...formData,
          themes: newThemes,
        }
      });
      return;
    }

    setFormData({
      ...formData,
      [fieldName]: value,
    });
  }

  return {
    isCreate,
    formData,
    previewFileComesFromBackend,
    setFormValue,
    submit,
  };
}

export default useHandleForm;